import React, { useState } from 'react';
import { useLanguage } from '../components/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronDown, 
  ChevronUp, 
  HelpCircle, 
  MessageCircle, 
  Mail, 
  Phone,
  Camera,
  CreditCard,
  Shield,
  Clock,
  Users,
  Zap,
  Star,
  CheckCircle,
  Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const faqCategories = [
  {
    id: 'general',
    name_he: 'שאלות כלליות',
    name_en: 'General Questions',
    icon: HelpCircle,
    color: 'violet'
  },
  {
    id: 'pricing',
    name_he: 'תמחור ותשלומים',
    name_en: 'Pricing & Payments',
    icon: CreditCard,
    color: 'emerald'
  },
  {
    id: 'technical',
    name_he: 'תמיכה טכנית',
    name_en: 'Technical Support',
    icon: Shield,
    color: 'blue'
  },
  {
    id: 'events',
    name_he: 'אירועים והזמנות',
    name_en: 'Events & Bookings',
    icon: Calendar,
    color: 'pink'
  }
];

const faqData = {
  general: [
    {
      q_he: 'מה זה AI Photo Booth?',
      q_en: 'What is AI Photo Booth?',
      a_he: 'AI Photo Booth היא עמדת צילום חדשנית המשתמשת בבינה מלאכותית כדי להפוך את המשתמשים לדמויות שונות. המערכת לוכדת תמונה והופכת אותה ליצירת אמנות ייחודית תוך שמירה על הזהות הפנים שלכם.',
      a_en: 'AI Photo Booth is an innovative photo booth that uses artificial intelligence to transform users into various characters. The system captures a photo and transforms it into a unique work of art while preserving facial identity.'
    },
    {
      q_he: 'כמה זמן לוקח ליצור תמונה?',
      q_en: 'How long does it take to create a photo?',
      a_he: 'תהליך היצירה לוקח בין 30 שניות ל-2 דקות, בהתאם לאיכות הנבחרת ולעומס השרתים. איכות רגילה (HD) לוקחת כ-30 שניות, בעוד איכות 4K Ultra יכולה לקחת עד 2 דקות.',
      a_en: 'The creation process takes between 30 seconds to 2 minutes, depending on the selected quality and server load. Standard (HD) quality takes about 30 seconds, while 4K Ultra quality can take up to 2 minutes.'
    },
    {
      q_he: 'האם התמונות נשמרות?',
      q_en: 'Are the photos saved?',
      a_he: 'כן, כל התמונות נשמרות בגלריה האישית שלכם. תוכלו לגשת אליהן בכל עת, לשתף אותן ברשתות החברתיות או להוריד אותן באיכות גבוהה.',
      a_en: 'Yes, all photos are saved in your personal gallery. You can access them anytime, share them on social media, or download them in high quality.'
    }
  ],
  pricing: [
    {
      q_he: 'כמה עולה כל יצירה?',
      q_en: 'How much does each creation cost?',
      a_he: 'יצירה באיכות רגילה (HD) עולה קרדיט אחד, ויצירה באיכות 4K Ultra עולה 2 קרדיטים. המחיר לקרדיט נע בין $0.07 ל-$0.20 בהתאם לגודל החבילה שתרכוש.',
      a_en: 'A Standard (HD) creation costs 1 credit, and a 4K Ultra creation costs 2 credits. The price per credit ranges from $0.07 to $0.20 depending on the package size you purchase.'
    },
    {
      q_he: 'האם הקרדיטים פגים?',
      q_en: 'Do credits expire?',
      a_he: 'לא, הקרדיטים שלכם לעולם לא פגים. תוכלו להשתמש בהם בכל עת שתרצו, ללא מגבלת זמן.',
      a_en: 'No, your credits never expire. You can use them anytime you want, without time limitations.'
    },
    {
      q_he: 'אילו שיטות תשלום קיימות?',
      q_en: 'What payment methods are available?',
      a_he: 'אנו מקבלים מגוון רחב של שיטות תשלום: כרטיסי אשראי (ויזה, מאסטרקארד, אמריקן אקספרס), Apple Pay, Google Pay, PayPal, Bit (בישראל בלבד), ומטבעות דיגיטליים (ביטקוין, אתריום, USDT).',
      a_en: 'We accept a wide range of payment methods: Credit cards (Visa, Mastercard, Amex), Apple Pay, Google Pay, PayPal, Bit (Israel only), and cryptocurrencies (Bitcoin, Ethereum, USDT).'
    },
    {
      q_he: 'האם יש החזר כספי?',
      q_en: 'Is there a refund policy?',
      a_he: 'כן, אנו מציעים החזר מלא תוך 7 ימים אם לא השתמשת בקרדיטים. אם כן השתמשת, נשמח לבדוק כל מקרה לגופו.',
      a_en: 'Yes, we offer a full refund within 7 days if you haven\'t used any credits. If you have used them, we\'ll be happy to review each case individually.'
    }
  ],
  technical: [
    {
      q_he: 'מה האיכות של התמונות?',
      q_en: 'What is the quality of the photos?',
      a_he: 'אנו מציעים שתי איכויות: HD (1920x1080) ו-4K Ultra (3840x2160). שתי האפשרויות מספקות תוצאות מרשימות, כאשר 4K מומלצת להדפסה או למסכים גדולים.',
      a_en: 'We offer two qualities: HD (1920x1080) and 4K Ultra (3840x2160). Both options provide impressive results, with 4K recommended for printing or large displays.'
    },
    {
      q_he: 'האם האתר תומך במובייל?',
      q_en: 'Is the site mobile-friendly?',
      a_he: 'כן, האתר מותאם במלואו למובייל. תוכלו להשתמש בשירות מכל מכשיר, בין אם זה סמארטפון או טאבלט.',
      a_en: 'Yes, the site is fully mobile-responsive. You can use the service from any device, whether it\'s a smartphone or tablet.'
    },
    {
      q_he: 'מה עושים אם התמונה לא יצאה טוב?',
      q_en: 'What if the photo doesn\'t turn out well?',
      a_he: 'אם אתם לא מרוצים מהתוצאה, תוכלו לנסות שוב - הקרדיט יחזור לחשבון שלכם אם התמונה לא נוצרה כראוי. אנו ממליצים להשתמש בתאורה טובה ובתמונה ברורה לקבלת התוצאות הטובות ביותר.',
      a_en: 'If you\'re not satisfied with the result, you can try again - the credit will be returned to your account if the photo wasn\'t created properly. We recommend using good lighting and a clear photo for the best results.'
    }
  ],
  events: [
    {
      q_he: 'האם אפשר להזמין אתכם לאירוע?',
      q_en: 'Can I book you for an event?',
      a_he: 'כן! אנו מציעים שירותי הפעלה לאירועים: חתונות, בר מצוות, ימי הולדת, אירועי חברה וכנסים. צור קשר איתנו לקבלת הצעה מותאמת.',
      a_en: 'Yes! We offer operation services for events: weddings, bar/bat mitzvahs, birthdays, corporate events, and conferences. Contact us for a customized proposal.'
    },
    {
      q_he: 'מה כוללת הפעלה באירוע?',
      q_en: 'What does event operation include?',
      a_he: 'הפעלה באירוע כוללת: ציוד מקצועי, מפעיל מומחה, התאמה אישית של הדמויות לאירוע, הדפסה מיידית במקום, ואפשרות למותג האירוע על התמונות.',
      a_en: 'Event operation includes: Professional equipment, expert operator, custom character adaptation for the event, on-site printing, and option to brand the event on the photos.'
    },
    {
      q_he: 'כמה זמן מראש צריך להזמין?',
      q_en: 'How far in advance should I book?',
      a_he: 'מומלץ להזמין לפחות חודש מראש, במיוחד בעונה העמוסה (קיץ וחגים). אנו עושים מאמצים להתאים את עצמנו גם להזמנות בקצר המרץ.',
      a_en: 'We recommend booking at least a month in advance, especially during the busy season (summer and holidays). We make efforts to accommodate last-minute bookings as well.'
    }
  ]
};

export default function FAQ() {
  const { language, isRTL } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState('general');
  const [expandedItems, setExpandedItems] = useState({});
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const toggleItem = (category, index) => {
    const key = `${category}-${index}`;
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    // Handle contact form submission
    console.log('Contact form submitted:', contactForm);
    // Reset form
    setContactForm({ name: '', email: '', subject: '', message: '' });
  };

  const currentCategory = faqCategories.find(cat => cat.id === selectedCategory);
  const currentFAQs = faqData[selectedCategory] || [];

  return (
    <div className="min-h-screen py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-500/10 border border-violet-500/20 mb-6">
            <HelpCircle className="w-4 h-4 text-violet-400" />
            <span className="text-sm font-medium text-violet-400">
              {language === 'he' ? 'מרכז עזרה' : 'Help Center'}
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {language === 'he' ? 'שאלות נפוצות' : 'Frequently Asked Questions'}
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            {language === 'he' 
              ? 'מצאת תשובות לשאלות הנפוצות ביותר או צור איתנו קשר לעזרה אישית'
              : 'Find answers to the most common questions or contact us for personal assistance'
            }
          </p>
        </motion.div>

        {/* Categories */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {faqCategories.map((category) => {
            const Icon = category.icon;
            return (
              <motion.button
                key={category.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedCategory(category.id)}
                className={`p-4 rounded-xl border-2 transition-all ${
                  selectedCategory === category.id
                    ? `bg-${category.color}-600/20 border-${category.color}-500/50`
                    : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                }`}
              >
                <Icon className={`w-6 h-6 mx-auto mb-2 text-${
                  selectedCategory === category.id ? category.color : 'slate-400'
                }-400`} />
                <span className={`text-sm font-medium ${
                  selectedCategory === category.id ? 'text-white' : 'text-slate-300'
                }`}>
                  {language === 'he' ? category.name_he : category.name_en}
                </span>
              </motion.button>
            );
          })}
        </div>

        {/* FAQ Items */}
        <div className="space-y-4 mb-16">
          {currentFAQs.map((faq, index) => {
            const key = `${selectedCategory}-${index}`;
            const isExpanded = expandedItems[key];
            
            return (
              <motion.div
                key={key}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="glass-card rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => toggleItem(selectedCategory, index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-slate-800/50 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-white pr-4">
                    {language === 'he' ? faq.q_he : faq.q_en}
                  </h3>
                  <div className={`transform transition-transform ${
                    isExpanded ? 'rotate-180' : ''
                  }`}>
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  </div>
                </button>
                
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-4 text-slate-300 leading-relaxed">
                        {language === 'he' ? faq.a_he : faq.a_en}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card rounded-2xl p-8"
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">
              {language === 'he' ? 'עדיין יש לך שאלות?' : 'Still have questions?'}
            </h2>
            <p className="text-slate-400">
              {language === 'he' 
                ? 'צוות התמיכה שלנו כאן לעזור'
                : 'Our support team is here to help'
              }
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Contact Form */}
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <Input
                type="text"
                placeholder={language === 'he' ? 'שם מלא' : 'Full Name'}
                value={contactForm.name}
                onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                className="bg-slate-800/50 border-slate-700 text-white"
                required
              />
              <Input
                type="email"
                placeholder={language === 'he' ? 'דוא"ל' : 'Email'}
                value={contactForm.email}
                onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                className="bg-slate-800/50 border-slate-700 text-white"
                required
              />
              <Input
                type="text"
                placeholder={language === 'he' ? 'נושא' : 'Subject'}
                value={contactForm.subject}
                onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                className="bg-slate-800/50 border-slate-700 text-white"
                required
              />
              <Textarea
                placeholder={language === 'he' ? 'הודעה' : 'Message'}
                value={contactForm.message}
                onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                className="bg-slate-800/50 border-slate-700 text-white min-h-[120px] resize-none"
                required
              />
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-700 hover:to-pink-700 text-white"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                {language === 'he' ? 'שלח הודעה' : 'Send Message'}
              </Button>
            </form>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-violet-600/20 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-violet-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">
                    {language === 'he' ? 'דוא"ל' : 'Email'}
                  </h3>
                  <p className="text-slate-400">support@aiphotobooth.com</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-600/20 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">
                    {language === 'he' ? 'טלפון' : 'Phone'}
                  </h3>
                  <p className="text-slate-400">
                    {language === 'he' ? '03-1234567' : '+972-3-1234567'}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    {language === 'he' ? 'א-ה 9:00-18:00' : 'Sun-Thu 9:00-18:00'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-600/20 flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">
                    {language === 'he' ? 'צ\\\'אט חי' : 'Live Chat'}
                  </h3>
                  <p className="text-slate-400">
                    {language === 'he' 
                      ? 'זמין 24/7 למשתמשים רשומים'
                      : 'Available 24/7 for registered users'
                    }
                  </p>
                </div>
              </div>

              <div className="p-4 bg-slate-800/30 rounded-xl border border-slate-700">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm font-medium text-yellow-400">
                    {language === 'he' ? 'תגובה מהירה' : 'Quick Response'}
                  </span>
                </div>
                <p className="text-xs text-slate-400">
                  {language === 'he' 
                    ? 'אנו מתחייבים להגיב לכל פנייה תוך 24 שעות'
                    : 'We commit to responding to every inquiry within 24 hours'
                  }
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
